import { useEffect, useRef, useState } from "react";
import type { StopResumeResult } from "../hooks/useAdminClaws";
import type { OpenClaw } from "../types/openclaw";
import styles from "./StopConfirmModal.module.css";

type Props = {
  open: boolean;
  claw: OpenClaw | null;
  onClose: () => void;
  onConfirm: (tenantId: string) => Promise<StopResumeResult>;
};

// Confirm dialog for pausing (stopping) a tenant instance. No type-to-confirm
// gate (unlike DeleteModal) because a stop is fully reversible via Resume.
export const StopConfirmModal = ({ open, claw, onClose, onConfirm }: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setServerError(null);
      setSubmitting(false);
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  if (!open || !claw) return null;

  const handleSubmit = async (evt: React.FormEvent): Promise<void> => {
    evt.preventDefault();
    setSubmitting(true);
    setServerError(null);
    const result = await onConfirm(claw.tenantId);
    setSubmitting(false);
    if (result.ok) {
      onClose();
      return;
    }
    setServerError(result.message);
  };

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={(evt) => {
        evt.preventDefault();
        if (!submitting) onClose();
      }}
    >
      <form method="dialog" className={styles.form} onSubmit={handleSubmit}>
        <header className={styles.header}>
          <h2 className={styles.title}>Pause instance</h2>
          <p className={styles.subtitle}>
            This stops <strong>{claw.name}</strong>'s instance to cut its running
            cost. The customer will see it as still being set up and lose access
            until you resume it. All data is kept &mdash; resuming brings it back.
          </p>
        </header>

        {serverError && <div className={styles.errorBanner}>{serverError}</div>}

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button type="submit" className={styles.confirmBtn} disabled={submitting}>
            {submitting ? "Stopping…" : "Stop instance"}
          </button>
        </footer>
      </form>
    </dialog>
  );
};
